import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError, tap } from 'rxjs/operators';

export interface BalancesReportVesQuery {
  fecha: string; // Formato: YYYY-MM-DD
}

@Injectable({
  providedIn: 'root',
})
export class BalancesReportVesService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 401:
          errorMessage =
            'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 400:
          errorMessage = 'Los parámetros proporcionados no son válidos.';
          break;
        case 404:
          errorMessage = 'El servicio no está disponible en este momento.';
          break;
        case 500:
          errorMessage =
            'Error interno del servidor. Por favor, intente más tarde.';
          break;
        default:
          if (error.error?.message) {
            errorMessage = error.error.message;
          }
          break;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  private validateQuery(query: BalancesReportVesQuery): string | null {
    if (!query.fecha) {
      return 'La fecha es requerida';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(query.fecha)) {
      return 'La fecha debe tener el formato YYYY-MM-DD';
    }
    return null;
  }

  /**
   * Descarga el reporte de saldos de usuarios VE en formato XLSX
   */
  downloadBalancesReport(query: BalancesReportVesQuery): Observable<Blob> {
    const validationError = this.validateQuery(query);
    if (validationError) {
      return throwError(() => ({ message: validationError }));
    }

    const params = new HttpParams().set('fecha', query.fecha);

    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get(`${this.apiUrl}balances-report-ves/export`, {
          headers,
          params,
          responseType: 'blob',
          observe: 'response',
        });
      }),
      switchMap((response) => {
        if (response.body) {
          return [response.body];
        }
        throw new Error('No se recibió contenido en la respuesta');
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  /**
   * Descarga el reporte y lo guarda automáticamente en el navegador
   */
  downloadAndSaveReport(
    query: BalancesReportVesQuery,
    customFilename?: string,
  ): Observable<void> {
    return this.downloadBalancesReport(query).pipe(
      tap((blob) => {
        const filename =
          customFilename || `saldos-usuarios-ve-${query.fecha}.xlsx`;

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        window.URL.revokeObjectURL(url);
      }),
      switchMap(() => [undefined]),
    );
  }
}
